/**
 * The selective review behind "Review N suggestions": one draft per proposed
 * change (included or not, and the text the user may have edited), shared by
 * the editor sheet controller and the onboarding step so the two save the
 * same way. Pure: no React, no platform code.
 *
 * Nothing here writes. `toApplyItems` turns the drafts into the items of one
 * `POST /reviews/:id/apply` call: only included changes, the edited text when
 * it differs from the suggestion, and a link normalised to its canonical URL
 * (an invalid link stops the save instead of being sent).
 */
import type { CvHealthApplyItem, CvHealthFinding } from "@tabbio/types";
import { normalizeCvHealthLink } from "./cv-health-editor";

export interface CvHealthReviewDraft {
  findingKey: string;
  include: boolean;
  /** The text the change will write: the suggestion, or the user's edit. */
  text: string;
}

/**
 * Drafts for `findings` (those with a proposal), in order. A draft kept from
 * an earlier pass over the same finding wins, so "Continue reviewing" keeps
 * the user's ticks and edits; a finding whose suggestion changed starts over.
 */
export function createReviewDrafts(
  findings: readonly CvHealthFinding[],
  previous: readonly CvHealthReviewDraft[] = [],
  previousFindings: readonly CvHealthFinding[] = [],
): CvHealthReviewDraft[] {
  const kept = new Map(previous.map((draft) => [draft.findingKey, draft]));
  const before = new Map(previousFindings.map((finding) => [finding.key, finding.proposal?.after]));
  return findings
    .filter((finding) => finding.proposal !== null)
    .map((finding) => {
      const draft = kept.get(finding.key);
      const sameSuggestion = before.has(finding.key)
        ? before.get(finding.key) === finding.proposal?.after
        : true;
      if (draft && sameSuggestion) return draft;
      return { findingKey: finding.key, include: true, text: finding.proposal?.after ?? "" };
    });
}

export function countSelectedDrafts(drafts: readonly CvHealthReviewDraft[]): number {
  return drafts.filter((draft) => draft.include).length;
}

/** True when any draft's text differs from its suggestion (asks before discarding). */
export function hasEditedDrafts(
  findings: readonly CvHealthFinding[],
  drafts: readonly CvHealthReviewDraft[],
): boolean {
  const byKey = new Map(findings.map((finding) => [finding.key, finding]));
  return drafts.some((draft) => {
    const after = byKey.get(draft.findingKey)?.proposal?.after ?? "";
    return draft.text.trim() !== after.trim();
  });
}

export type CvHealthApplyItemsResult =
  | { ok: true; items: CvHealthApplyItem[] }
  | { ok: false; error: "invalidLink" | "empty"; findingKey: string };

/** The included drafts as apply items, or the first draft that cannot be saved. */
export function toApplyItems(
  findings: readonly CvHealthFinding[],
  drafts: readonly CvHealthReviewDraft[],
): CvHealthApplyItemsResult {
  const byKey = new Map(findings.map((finding) => [finding.key, finding]));
  const items: CvHealthApplyItem[] = [];
  for (const draft of drafts) {
    if (!draft.include) continue;
    const proposal = byKey.get(draft.findingKey)?.proposal;
    if (!proposal) continue;
    let value = draft.text.trim();
    if (!value) return { ok: false, error: "empty", findingKey: draft.findingKey };
    if (proposal.target.kind === "link") {
      const normalized = normalizeCvHealthLink(proposal.target.key, value);
      if (!normalized) return { ok: false, error: "invalidLink", findingKey: draft.findingKey };
      value = normalized;
    }
    items.push(
      value === proposal.after.trim()
        ? { findingKey: draft.findingKey }
        : { findingKey: draft.findingKey, text: value },
    );
  }
  return { ok: true, items };
}

/**
 * A selection carried across the plan chooser (onboarding), matched against
 * the report the fix round ends up with: an item is applied only when the
 * same finding still proposes a change to the same original text, so an
 * edit is never written over a CV that moved on. The rest come back as
 * `dropped`.
 */
export interface CvHealthCarriedItem {
  findingKey: string;
  /** The original the user reviewed (`proposal.before`). */
  before: string;
  /** The edited text, when it differs from the suggestion. */
  text?: string;
}

export function matchCarriedItems(
  findings: readonly CvHealthFinding[],
  carried: readonly CvHealthCarriedItem[],
): { items: CvHealthApplyItem[]; dropped: number } {
  const byKey = new Map(findings.map((finding) => [finding.key, finding]));
  const items: CvHealthApplyItem[] = [];
  let dropped = 0;
  for (const entry of carried) {
    const finding = byKey.get(entry.findingKey);
    const proposal = finding?.decision === null ? finding.proposal : null;
    if (!proposal || proposal.before.trim() !== entry.before.trim()) {
      dropped += 1;
      continue;
    }
    items.push(entry.text ? { findingKey: entry.findingKey, text: entry.text } : { findingKey: entry.findingKey });
  }
  return { items, dropped };
}

/** The apply items of a review, with the original each one changes, for carrying across the chooser. */
export function toCarriedItems(
  findings: readonly CvHealthFinding[],
  items: readonly CvHealthApplyItem[],
): CvHealthCarriedItem[] {
  const byKey = new Map(findings.map((finding) => [finding.key, finding]));
  return items.flatMap((item) => {
    const proposal = byKey.get(item.findingKey)?.proposal;
    if (!proposal) return [];
    return [
      item.text
        ? { findingKey: item.findingKey, before: proposal.before, text: item.text }
        : { findingKey: item.findingKey, before: proposal.before },
    ];
  });
}
